import { createFileRoute, useRouter, Link } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  getSearchAnalytics,
  resubmitSitemaps,
  listSitemaps,
  validateSitemapStatus,
  checkDeployedCronVersion,
  purgeLegacyProperty,
} from "@/lib/gsc.functions";
import { getHostHealth } from "@/lib/host-health.functions";
import { runIndexWatchCheck, runLinkAuditCheck } from "@/lib/seo-monitors.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

export const Route = createFileRoute("/seo-dashboard")({
  component: SeoDashboard,
  head: () => ({
    meta: [
      { title: "SEO Dashboard — Vending Machine Free" },
      { name: "description", content: "Internal SEO performance dashboard." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function SeoDashboard() {
  const router = useRouter();
  const analytics = useServerFn(getSearchAnalytics);
  const resubmit = useServerFn(resubmitSitemaps);
  const sitemaps = useServerFn(listSitemaps);
  const validate = useServerFn(validateSitemapStatus);
  const cronCheck = useServerFn(checkDeployedCronVersion);
  const purge = useServerFn(purgeLegacyProperty);
  const hostHealthFn = useServerFn(getHostHealth);
  const hostHealth = useQuery({ queryKey: ["seo", "host-health"], queryFn: () => hostHealthFn() });
  const indexWatchFn = useServerFn(runIndexWatchCheck);
  const linkAuditFn = useServerFn(runLinkAuditCheck);
  const indexWatch = useQuery({
    queryKey: ["seo", "index-watch"],
    queryFn: () => indexWatchFn(),
    enabled: false,
  });
  const linkAudit = useQuery({
    queryKey: ["seo", "link-audit"],
    queryFn: () => linkAuditFn(),
    enabled: false,
  });


  const byDate = useQuery({
    queryKey: ["gsc", "date"],
    queryFn: () => analytics({ data: { dimensions: ["date"], days: 28, rowLimit: 100 } }),
  });
  const byQuery = useQuery({
    queryKey: ["gsc", "query"],
    queryFn: () => analytics({ data: { dimensions: ["query"], days: 28, rowLimit: 25 } }),
  });
  const byPage = useQuery({
    queryKey: ["gsc", "page"],
    queryFn: () => analytics({ data: { dimensions: ["page"], days: 28, rowLimit: 25 } }),
  });
  const smList = useQuery({ queryKey: ["gsc", "sitemaps"], queryFn: () => sitemaps() });
  const cronVersion = useQuery({ queryKey: ["gsc", "cron-version"], queryFn: () => cronCheck() });
  const validation = useQuery({
    queryKey: ["gsc", "validate"],
    queryFn: () => validate(),
    enabled: false,
  });

  const resub = useMutation({
    mutationFn: () => resubmit(),
    onSuccess: async (d) => {
      const ok = d.results.filter((r) => r.status < 300).length;
      toast.success(`Resubmitted ${ok}/${d.results.length} sitemaps — validating…`);
      await validation.refetch();
      router.invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const purgeMut = useMutation({
    mutationFn: () => purge(),
    onSuccess: async (d) => {
      const ok = d.results.filter((r) => r.status < 300).length;
      toast.success(`Purged ${ok}/${d.results.length} legacy entries`);
      await validation.refetch();
      router.invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dateRows = (byDate.data?.rows ?? []).map((r) => ({
    date: r.keys[0],
    clicks: r.clicks,
    impressions: r.impressions,
    position: Number(r.position.toFixed(1)),
  }));

  const totals = dateRows.reduce(
    (a, r) => ({ clicks: a.clicks + r.clicks, impressions: a.impressions + r.impressions }),
    { clicks: 0, impressions: 0 },
  );
  const avgCtr = totals.impressions ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : "0.00";

  return (
    <div className="container mx-auto max-w-7xl px-4 py-10 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">SEO Dashboard</h1>
          <p className="text-muted-foreground text-sm">
            Google Search Console — last 28 days · vendingmachinefree.com
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => validation.refetch()} disabled={validation.isFetching}>
            {validation.isFetching ? "Validating…" : "Validate status"}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (confirm("Delete ALL sitemap entries registered under vendingmachines.free? This cannot be undone from here.")) {
                purgeMut.mutate();
              }
            }}
            disabled={purgeMut.isPending}
          >
            {purgeMut.isPending ? "Purging…" : "Purge legacy property"}
          </Button>
          <Button onClick={() => resub.mutate()} disabled={resub.isPending}>
            {resub.isPending ? "Resubmitting…" : "Resubmit all sitemaps"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>SEO health — redirects, SSL &amp; file parity</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-4">
          {hostHealth.isLoading ? (
            <p className="text-muted-foreground">Probing both hosts…</p>
          ) : hostHealth.data ? (
            <>
              <div className="space-y-1">
                <p className="font-medium">Reachability</p>
                <HostRow label="vendingmachinefree.com" probe={hostHealth.data.apex} />
                <HostRow label="www.vendingmachinefree.com" probe={hostHealth.data.www} />
              </div>

              <div className="space-y-1">
                <p className="font-medium">SSL / certificate</p>
                {hostHealth.data.tls.map((t) => (
                  <p
                    key={t.host}
                    className={t.valid ? "text-green-600" : "text-red-600"}
                  >
                    {t.valid ? "✓" : "✗"} {t.host.replace("https://", "")} —{" "}
                    {t.valid ? "valid certificate, handshake OK" : (t.error ?? "handshake failed")}
                  </p>
                ))}
              </div>

              <div className="space-y-1">
                <p className="font-medium">Redirect chain (expect 301 → https apex)</p>
                {hostHealth.data.redirects.map((r) => (
                  <p key={r.url} className={r.clean ? "text-green-600" : "text-amber-600"}>
                    {r.clean ? "✓" : "⚠"} {r.label} —{" "}
                    {r.error
                      ? r.error
                      : `${r.status ?? "no status"}${r.location ? ` → ${r.location}` : ""}`}
                  </p>
                ))}
              </div>

              <div className="space-y-1">
                <p className="font-medium">Bot file parity (apex vs www)</p>
                {hostHealth.data.files.map((f) => (
                  <p key={f.file} className={f.consistent ? "text-green-600" : "text-amber-600"}>
                    {f.consistent ? "✓" : "⚠"} {f.file} — apex {f.apexStatus ?? "—"} · www{" "}
                    {f.wwwStatus ?? "—"} · {f.note}
                  </p>
                ))}
              </div>

              <div className="space-y-1">
                <p className="font-medium">
                  Canonical consistency (meta tag + Link header, apex vs www)
                </p>
                {hostHealth.data.canonicals.map((c) => (
                  <p key={c.path} className={c.consistent ? "text-green-600" : "text-amber-600"}>
                    {c.consistent ? "✓" : "⚠"} {c.path} — {c.note}
                    {c.apexHeaderCanonical || c.wwwHeaderCanonical ? (
                      <span className="text-muted-foreground">
                        {" "}
                        (Link header: {c.apexHeaderCanonical ?? c.wwwHeaderCanonical})
                      </span>
                    ) : null}
                  </p>
                ))}
              </div>

              {!hostHealth.data.wwwReachable && (
                <p className="text-muted-foreground">
                  Add <code>www.vendingmachinefree.com</code> in Project settings → Domains (A
                  record <code>www</code> → 185.158.133.1, apex stays Primary) so www gets a
                  certificate; the app then 301-redirects it to the apex.
                </p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">Check failed.</p>
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => hostHealth.refetch()}>
              Re-check
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link to="/dns-wizard">Open DNS &amp; SSL wizard</Link>
            </Button>
          </div>
        </CardContent>
      </Card>


      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Index watch — key templates</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            {indexWatch.data ? (
              <>
                <p
                  className={
                    indexWatch.data.failures === 0
                      ? "text-green-600 font-medium"
                      : "text-red-600 font-medium"
                  }
                >
                  {indexWatch.data.failures} of {indexWatch.data.checked} templates failing
                  {indexWatch.data.spiked ? " · spike detected" : ""}
                </p>
                <ul className="space-y-0.5 text-xs">
                  {indexWatch.data.probes.map((p) => (
                    <li key={p.url} className={p.failed ? "text-red-600" : "text-muted-foreground"}>
                      {p.failed ? "✗" : "✓"} {p.template} —{" "}
                      {p.error ?? (p.coverageState || p.verdict)}

                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-muted-foreground">
                Inspects one URL per template (home, state, city, area, industry, hub) in Search
                Console. Runs automatically on a schedule.
              </p>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => indexWatch.refetch()}
              disabled={indexWatch.isFetching}
            >
              {indexWatch.isFetching ? "Inspecting…" : "Run index check"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Canonical &amp; internal link audit</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            {linkAudit.data ? (
              <>
                <p
                  className={
                    linkAudit.data.problems === 0
                      ? "text-green-600 font-medium"
                      : "text-red-600 font-medium"
                  }
                >
                  {linkAudit.data.problems} problem(s) across {linkAudit.data.pagesChecked} sampled
                  state/city/area pages
                </p>
                <ul className="space-y-0.5 text-xs">
                  {linkAudit.data.pages
                    .filter((p) => p.problems.length > 0)
                    .map((p) => (
                      <li key={p.path} className="text-red-600">
                        {p.path} — {p.problems.join("; ")}
                      </li>
                    ))}
                </ul>
              </>
            ) : (
              <p className="text-muted-foreground">
                Checks that every state page self-references its canonical and og:url on the apex
                host and links internally to the same host.
              </p>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => linkAudit.refetch()}
              disabled={linkAudit.isFetching}
            >
              {linkAudit.isFetching ? "Auditing…" : "Run link audit"}
            </Button>
          </CardContent>
        </Card>
      </div>




      <Card>
        <CardHeader>
          <CardTitle>Weekly cron target check</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          {cronVersion.isLoading ? (
            <p className="text-muted-foreground">Probing published endpoint…</p>
          ) : cronVersion.data?.isCurrent ? (
            <>
              <p className="text-green-600 font-medium">
                ✓ Published cron targets <code>{cronVersion.data.target}</code> only (version {cronVersion.data.deployedVersion}).
              </p>
              <p className="text-muted-foreground">Monday's run will submit sitemaps to the canonical property and DELETE any legacy submissions.</p>
            </>
          ) : (
            <>
              <p className="text-amber-600 font-medium">
                ⚠ Deployed version: <code>{cronVersion.data?.deployedVersion}</code> · expected: <code>{cronVersion.data?.expectedVersion}</code>
              </p>
              <p className="text-muted-foreground">{cronVersion.data?.target}</p>
              <p className="text-muted-foreground">Publish the app so the weekly cron picks up the canonical-only submitter.</p>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => cronVersion.refetch()} className="mt-1">
            Re-check
          </Button>
        </CardContent>
      </Card>

      {validation.data && (
        <Card>
          <CardHeader>
            <CardTitle>
              Post-resubmit validation
              {validation.data.issues.length === 0 ? (
                <span className="ml-2 text-sm font-normal text-green-600">✓ clean</span>
              ) : (
                <span className="ml-2 text-sm font-normal text-red-600">
                  {validation.data.issues.length} issue(s)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-3">
            {validation.data.properties.map((p) => (
              <div key={p.host}>
                <p className="font-medium">
                  {p.host}{" "}
                  <span className="text-muted-foreground font-normal">({p.sitemaps.length} entries)</span>
                </p>
                {p.sitemaps.length === 0 && (
                  <p className="text-muted-foreground text-xs">No sitemaps registered.</p>
                )}
                {p.sitemaps.some((s) => s.wrongHost || s.errors > 0 || s.warnings > 0) && (
                  <ul className="text-xs mt-1 space-y-0.5">
                    {p.sitemaps
                      .filter((s) => s.wrongHost || s.errors > 0 || s.warnings > 0)
                      .map((s) => (
                        <li key={s.path} className="text-red-600">
                          {s.wrongHost && "⚠ wrong host · "}
                          {s.errors > 0 && `${s.errors} err · `}
                          {s.warnings > 0 && `${s.warnings} warn · `}
                          <span className="text-muted-foreground">{s.path}</span>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Clicks (28d)" value={totals.clicks.toLocaleString()} />
        <Stat label="Impressions (28d)" value={totals.impressions.toLocaleString()} />
        <Stat label="Avg CTR" value={`${avgCtr}%`} />
        <Stat label="Sitemaps" value={String(smList.data?.sitemap?.length ?? 0)} />
      </div>


      <Card>
        <CardHeader>
          <CardTitle>Clicks & Impressions over time</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          {byDate.isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : dateRows.length === 0 ? (
            <p className="text-muted-foreground">No data yet — Google typically takes 2–7 days after verification.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dateRows}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" fontSize={11} />
                <YAxis yAxisId="l" fontSize={11} />
                <YAxis yAxisId="r" orientation="right" fontSize={11} />
                <Tooltip />
                <Legend />
                <Line yAxisId="l" type="monotone" dataKey="clicks" stroke="hsl(var(--primary))" strokeWidth={2} />
                <Line yAxisId="r" type="monotone" dataKey="impressions" stroke="hsl(var(--accent-foreground))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <TableCard title="Top Queries" loading={byQuery.isLoading} rows={byQuery.data?.rows ?? []} keyLabel="Query" />
        <TableCard title="Top Pages" loading={byPage.isLoading} rows={byPage.data?.rows ?? []} keyLabel="Page" isUrl />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Submitted Sitemaps</CardTitle>
        </CardHeader>
        <CardContent>
          {smList.isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-4">Path</th>
                    <th className="py-2 pr-4">Last Submitted</th>
                    <th className="py-2 pr-4">Errors</th>
                    <th className="py-2 pr-4">Warnings</th>
                  </tr>
                </thead>
                <tbody>
                  {(smList.data?.sitemap ?? []).map((s, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-2 pr-4 truncate max-w-xs">{s.path}</td>
                      <td className="py-2 pr-4">{s.lastSubmitted?.slice(0, 10) ?? "—"}</td>
                      <td className="py-2 pr-4">{s.errors ?? 0}</td>
                      <td className="py-2 pr-4">{s.warnings ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-muted-foreground text-xs uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}

function TableCard({
  title,
  loading,
  rows,
  keyLabel,
  isUrl,
}: {
  title: string;
  loading: boolean;
  rows: Array<{ keys: string[]; clicks: number; impressions: number; ctr: number; position: number }>;
  keyLabel: string;
  isUrl?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground">No data yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">{keyLabel}</th>
                  <th className="py-2 pr-2 text-right">Clicks</th>
                  <th className="py-2 pr-2 text-right">Impr.</th>
                  <th className="py-2 pr-2 text-right">CTR</th>
                  <th className="py-2 pr-2 text-right">Pos.</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2 pr-4 truncate max-w-[220px]">
                      {isUrl ? (
                        <a href={r.keys[0]} target="_blank" rel="noopener" className="text-primary hover:underline">
                          {r.keys[0].replace(/^https?:\/\/[^/]+/, "")}
                        </a>
                      ) : (
                        r.keys[0]
                      )}
                    </td>
                    <td className="py-2 pr-2 text-right">{r.clicks}</td>
                    <td className="py-2 pr-2 text-right">{r.impressions}</td>
                    <td className="py-2 pr-2 text-right">{(r.ctr * 100).toFixed(1)}%</td>
                    <td className="py-2 pr-2 text-right">{r.position.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HostRow({
  label,
  probe,
}: {
  label: string;
  probe: {
    ok: boolean;
    status: number | null;
    redirectTo: string | null;
    error: string | null;
  };
}) {
  return (
    <p className={probe.ok ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
      {probe.ok ? "✓" : "✗"} {label} —{" "}
      {probe.error
        ? probe.error
        : probe.redirectTo
          ? `${probe.status} redirect → ${probe.redirectTo}`
          : `HTTP ${probe.status}`}
    </p>
  );
}
